import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Building2, MapPin, ExternalLink, HeartHandshake, Ticket, Search } from "lucide-react";

// Annuaire des associations locales (28) présentes sur HelloAsso.
export default function AssociationsLocales() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/helloasso/associations");
        setItems(data.items || []);
      } catch { /* silencieux */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = items.filter((o) => {
    const t = `${o.name} ${o.city} ${o.category}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="associations-page">
      <div className="flex items-center gap-2 text-[#E05638]">
        <HeartHandshake size={22} />
        <span className="text-xs font-bold uppercase tracking-widest">Vie associative locale</span>
      </div>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Les associations d'Eure-et-Loir</h1>
      <p className="mt-2 text-slate-500 text-sm max-w-2xl">
        Adhérez, achetez vos billets ou faites un don directement auprès des associations locales, via HelloAsso — gratuit pour les associations.
      </p>

      <div className="mt-5 relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une association, une ville…" className="pl-9" data-testid="assoc-search" />
      </div>

      {loading ? (
        <div className="mt-8 text-slate-500 text-sm">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-8 bg-white border border-dashed rounded-2xl p-10 text-center text-slate-500 text-sm" data-testid="assoc-empty">
          Aucune association à afficher pour le moment. Revenez bientôt !
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="assoc-grid">
          {filtered.map((o) => (
            <div key={o.slug} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col" data-testid={`assoc-card-${o.slug}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-slate-100 overflow-hidden grid place-items-center shrink-0">
                  {o.logo ? <img src={o.logo} alt={o.name} className="w-full h-full object-cover" loading="lazy" /> : <Building2 size={22} className="text-slate-400" />}
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-slate-900 leading-tight line-clamp-2">{o.name}</div>
                  {o.city && <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin size={12} /> {o.city}</div>}
                </div>
              </div>
              {o.description && <p className="mt-3 text-sm text-slate-600 line-clamp-3">{o.description}</p>}
              <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                <a href={o.url} target="_blank" rel="noreferrer" className="flex-1">
                  <Button size="sm" className="btn-shine text-white w-full" data-testid={`assoc-join-${o.slug}`}>
                    <HeartHandshake size={14} className="mr-1" /> Adhérer / Soutenir
                  </Button>
                </a>
                <a href={o.url} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" data-testid={`assoc-link-${o.slug}`}><ExternalLink size={14} /></Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
